import { makeAutoObservable } from "mobx";
import { RootStore, LangStore } from "../../../store";
import { StoreStatus, CallbackDataParams } from "../../../store/types";
import { getApi, getApiError, isSessionExpired } from "../../../api";
import { ViewEntityStore } from "../../common/store/types";
import { authorCollectingSocietyService } from "../services/authorCollectingSocietyService";
import { authorCollectingSocietyDisplayId } from "./AuthorCollectingSocietyCommonStore";
import { AuthorCollectingSociety } from "../../../api/graphql";
import { collectiveManagementSocietyDisplayId } from "../../collectiveManagementSociety/store/CollectiveManagementSocietyCommonStore";
import { territoryDisplayId } from "../../territory/store/TerritoryCommonStore";
import { collectiveManagementSocietyService } from "../../collectiveManagementSociety/services/collectiveManagementSocietyService";
import { territoryService } from "../../territory/services/territoryService";
import { InitializeParams } from "../../../store/types";

export class AuthorCollectingSocietyViewStore implements ViewEntityStore {
  readonly rootStore: RootStore;
  readonly langStore: LangStore;
  status: StoreStatus = "uninitialized";
  storeId?: string;
  originalError: any;
  errorDialogOpened: boolean = false;
  errorTitle: string = "Error";
  errorDescription: string = "Hubo un error";
  service = authorCollectingSocietyService;
  authorCollectingSocietyInstance: AuthorCollectingSociety | null = null;
  cardState: Record<string, boolean> = {};
  savedId: string | null = null;
  cancelled = false;
  isDrawerMode = false;
  isModalMode = false;
  callbackParams: CallbackDataParams | null = null;
  idValue: string | undefined;
  collectingSocietyService = collectiveManagementSocietyService;
  collectingSocietySearchValue: string = "";
  collectingSocietyIdValue: string = "";
  collectingSocietySelectedValue: { displayId: string; id: string } | null =
    null;
  memberNumberValue: string = "";
  territoriesService = territoryService;
  territoriesSearchValue: string = "";
  territoriesIdValue: string = "";
  territoriesSelectedValues: { displayId: string; id: string }[] = [];

  constructor(rootStore: RootStore, storeId?: string) {
    this.rootStore = rootStore;
    this.langStore = rootStore.langStore;
    this.storeId = storeId;

    makeAutoObservable(this);
  }

  destroy() {
    // this object will be unusable
  }

  setCallbackParams(callbackParams: CallbackDataParams | null) {
    if (callbackParams?.callerData) {
      this.callbackParams = callbackParams;
    }
  }

  toggleCardState(id: string) {
    this.cardState[id] = !this.cardState[id];
  }

  setCardState(id: string, status: boolean) {
    this.cardState[id] = status;
  }

  getCardState(id: string) {
    if (!(id in this.cardState)) {
      this.cardState[id] = true;
    }
    return !!this.cardState[id];
  }

  *loadAuthorCollectingSocietyById(id: string): any {
    const api = yield* getApi(
      this.rootStore.authStore,
      this.rootStore.activityStore
    );
    const result = yield api.authorCollectingSocietyOne({ id });
    this.authorCollectingSocietyInstance = result.authorCollectingSocietyOne;
    if (this.authorCollectingSocietyInstance) {
      this.loadValues(this.authorCollectingSocietyInstance);
    }
  }

  setStatus(status: StoreStatus) {
    this.status = status;
  }

  get processingTitle() {
    return this.langStore.processingTitle;
  }

  get processingDescription() {
    return this.langStore.processingDescription;
  }

  get displayId(): string {
    if (this.authorCollectingSocietyInstance) {
      return authorCollectingSocietyDisplayId(
        this.authorCollectingSocietyInstance,
        this.langStore
      );
    }
    return "";
  }

  get breadcrumbLabel(): string {
    const displayId = this.displayId;
    if (displayId) {
      return displayId;
    }
    return this.langStore.AuthorCollectingSocietySingular;
  }

  closeErrorDialog() {
    this.errorDialogOpened = false;
  }

  setContentTop(_contentTop: number) {}

  *returnedData(params: CallbackDataParams): any {
    if (
      (!params.storeId || params.storeId === this.storeId) &&
      params.returnData &&
      !params.returnData.cancelled &&
      params.returnData.resultId
    ) {
      if (params.callerData?.fieldName === "collectingSociety") {
        const api = yield* getApi(
          this.rootStore.authStore,
          this.rootStore.activityStore
        );
        const result = yield api.collectiveManagementSocietyOne({
          id: params.returnData.resultId,
        });
        const collectiveManagementSociety =
          result.collectiveManagementSocietyOne;
        this.collectingSocietySelectedValue = collectiveManagementSociety;
      } else if (params.callerData?.fieldName === "territories") {
        const api = yield* getApi(
          this.rootStore.authStore,
          this.rootStore.activityStore
        );
        const result = yield api.territoryOne({
          id: params.returnData.resultId,
        });
        const territory = result.territoryOne;
        this.territoriesSelectedValues.push(territory);
      }
    }
  }

  showErrorDialog(originalError?: any, title?: string, description?: string) {
    // isSessionExpired y no una comparación a mano: los llamadores pasan a
    // veces el Error crudo y a veces el código ya resuelto, y cada store tenía
    // su propia versión del chequeo —o ninguna—, así que la sesión vencida se
    // detectaba o no según por dónde hubiera entrado el error.
    if (isSessionExpired(originalError, this.langStore)) {
      this.rootStore.authStore.openSessionExpiredDialog(() => {});
      return;
    }

    this.originalError = originalError;
    if (title) {
      this.errorTitle = title;
    }
    if (description) {
      this.errorDescription = description;
    }
    this.errorDialogOpened = true;
  }

  get collectingSocietyValue() {
    if (this.collectingSocietySelectedValue) {
      return {
        id: this.collectingSocietySelectedValue.id,
        displayId: collectiveManagementSocietyDisplayId(
          this.collectingSocietySelectedValue,
          this.langStore
        ),
      };
    }
    return null;
  }

  get territoriesValues() {
    return this.territoriesSelectedValues.map((x) => {
      return {
        id: x.id,
        displayId: territoryDisplayId(x, this.langStore),
      };
    });
  }

  clearFields() {
    this.savedId = null;
    this.cancelled = false;
    this.authorCollectingSocietyInstance = null;
    this.collectingSocietySearchValue = "";
    this.collectingSocietyIdValue = "";
    this.memberNumberValue = "";
    this.territoriesSearchValue = "";
    this.territoriesIdValue = "";
    this.territoriesSelectedValues = [];
  }

  close() {
    if (this.isDrawerMode) {
      this.rootStore.closeDrawerView();
    } else if (this.isModalMode) {
      // In modal mode, close or pop the modal instead of navigating
      if (this.rootStore.modalViewStack.length > 1) {
        this.rootStore.popModalView();
      } else {
        this.rootStore.closeModalView();
      }
    } else {
      this.rootStore.router.goBack();
    }
  }

  cancelAuthorCollectingSociety() {
    this.cancelled = true;
    this.close();
  }

  get isEditEnabled(): boolean {
    return false;
  }

  editAuthorCollectingSociety() {
    const id = this.authorCollectingSocietyInstance?.id;
    if (!id) return;

    if (this.isDrawerMode) {
      this.rootStore.closeDrawerView();
    } else if (this.isModalMode) {
      this.rootStore.closeModalView();
    }
    this.rootStore.router.goTo(
      "/authorCollectingSociety/update/" + id,
      undefined,
      undefined,
      undefined,
      this.breadcrumbLabel
    );
  }

  printAuthorCollectingSociety(handlePrint: () => void): void {
    handlePrint();
  }

  *closeAuthorCollectingSociety(): any {
    yield this.close();
  }

  loadValues(instance: AuthorCollectingSociety) {
    this.idValue = instance.id;
    this.collectingSocietySelectedValue = instance.collectingSociety;
    this.memberNumberValue = instance.memberNumber;
    this.territoriesSelectedValues = instance.territories
      ? instance.territories
      : [];
  }

  getState(): InitializeParams {
    return {
      id: this.idValue,
    };
  }

  *initialize(params?: InitializeParams) {
    try {
      this.status = "processing";

      const id = params?.id;

      if (id) {
        yield this.loadAuthorCollectingSocietyById(id);
      }

      this.status = "ok";
    } catch (error) {
      this.status = "error";
      if (error instanceof Error) {
        const apiError = getApiError(error, this.langStore);
        let message = apiError.message;
        this.showErrorDialog(error, apiError.title, message);
      } else {
        this.showErrorDialog(error, this.langStore.serverError, undefined);
      }
    }
  }
}
